import {
    getCookie as getCookieNext,
    setCookie as setCookieNext,
    deleteCookie as deleteCookieNext,
} from "cookies-next";
import { CookieValueTypes } from "cookies-next/lib/types";
import { IncomingMessage, ServerResponse } from "http";
import { NextApiRequestCookies } from "next/dist/server/api-utils";

type GetCookieOptions = Parameters<typeof getCookieNext>[1];
type SetCookieOptions = Parameters<typeof setCookieNext>[2];
type DeleteCookieOptions = Parameters<typeof deleteCookieNext>[1];

const getSessionCookieOptions = (): {
    path: string;
    sameSite: "none" | "lax";
    secure: boolean;
} => {
    const isProduction = process.env.NODE_ENV === "production";

    return {
        path: "/",
        sameSite: isProduction ? "none" : "lax",
        secure: isProduction,
    };
};

const hasServerCookieContext = (
    options?: GetCookieOptions | SetCookieOptions | DeleteCookieOptions,
): boolean => {
    const context = options as
        | {
              req?: IncomingMessage;
              res?: ServerResponse;
          }
        | undefined;

    return Boolean(context?.req || context?.res);
};

// Browsers known to restrict cross-site cookies in their WebView environments.
// Add new patterns here as additional in-app browsers are identified.
const isRestrictedWebView = (userAgent?: string): boolean => {
    if (!userAgent) {
        return false;
    }

    return /TikTok|musical_ly|Instagram|FBAN|FBAV|Snapchat/i.test(userAgent);
};

const getUserAgentFromOptions = (
    options?: GetCookieOptions | SetCookieOptions | DeleteCookieOptions,
): string | undefined => {
    const headers = (options as { req?: IncomingMessage } | undefined)?.req
        ?.headers;

    if (!headers) {
        return undefined;
    }

    const userAgent = headers["user-agent"];
    if (typeof userAgent === "string") {
        return userAgent;
    }

    if (Array.isArray(userAgent) && typeof userAgent[0] === "string") {
        return userAgent[0];
    }

    return undefined;
};

const isRestrictedWebViewBrowser = (userAgentFromRequest?: string): boolean => {
    if (typeof window !== "undefined") {
        return isRestrictedWebView(navigator.userAgent);
    }

    return isRestrictedWebView(userAgentFromRequest);
};

const setCookie = (
    key: string,
    value: string | number,
    options?: SetCookieOptions,
): void => {
    setCookieNext(key, value, options);

    const userAgentFromRequest = getUserAgentFromOptions(options);
    if (
        isRestrictedWebViewBrowser(userAgentFromRequest) &&
        typeof window !== "undefined"
    ) {
        try {
            sessionStorage.setItem(key, String(value));
        } catch (error) {
            // Ignore storage write failures.
        }
    }
};

const getCookie = (
    key: string,
    options?: GetCookieOptions,
): CookieValueTypes => {
    const effectiveOptions = hasServerCookieContext(options)
        ? options
        : undefined;

    // On client without req/res context, read directly from document.cookie.
    const cookieValue =
        typeof window !== "undefined"
            ? getCookieNext(key)
            : getCookieNext(key, effectiveOptions);

    if (cookieValue) {
        return cookieValue;
    }

    const userAgentFromRequest = getUserAgentFromOptions(options);
    if (
        isRestrictedWebViewBrowser(userAgentFromRequest) &&
        typeof window !== "undefined"
    ) {
        try {
            return sessionStorage.getItem(key) ?? cookieValue;
        } catch (error) {
            return cookieValue;
        }
    }

    return cookieValue;
};

const deleteCookie = (key: string, options?: DeleteCookieOptions): void => {
    deleteCookieNext(key, options);

    const userAgentFromRequest = getUserAgentFromOptions(options);
    if (
        isRestrictedWebViewBrowser(userAgentFromRequest) &&
        typeof window !== "undefined"
    ) {
        try {
            sessionStorage.removeItem(key);
        } catch (error) {
            // Ignore storage cleanup failures.
        }
    }
};

export const setAccessToken = (
    accessToken: string | null,
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): void => {
    if (accessToken) {
        const cookieHoursLifeTime = process.env
            .ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS
            ? parseInt(process.env.ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS, 10)
            : 1;

        setCookie(`_tf_token`, accessToken, {
            req,
            res,
            maxAge: 60 * 60 * cookieHoursLifeTime,
            ...getSessionCookieOptions(),
        });
    }
};

export const setUserState = (
    state: string | null,
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): void => {
    if (state) {
        const cookieHoursLifeTime = process.env
            .ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS
            ? parseInt(process.env.ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS, 10)
            : 1;

        setCookie(`_tf_state`, state, {
            req,
            res,
            maxAge: 60 * 60 * cookieHoursLifeTime,
            ...getSessionCookieOptions(),
        });
    }
};

export const setOfferId = (
    oid: string | null,
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): void => {
    if (oid) {
        const cookieHoursLifeTime = process.env
            .ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS
            ? parseInt(process.env.ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS, 10)
            : 1;

        setCookie(`_tf_oid`, oid, {
            req,
            res,
            maxAge: 60 * 60 * cookieHoursLifeTime,
        });
    }
};
export const getOfferId = (
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): CookieValueTypes => {
    const _tf_oid = getCookie("_tf_oid", { req, res });
    return _tf_oid;
};

export const setLocaleCookie = (
    locale: string,
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): void => {
    setCookie(`_tf_locale`, locale, {
        req,
        res,
    });
};

export const getLocale = (
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): CookieValueTypes => {
    const locale = getCookie("_tf_locale", { req, res });
    return locale;
};

export const setVisitorId = (
    visitorId: string | null,
    req:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res: ServerResponse,
): void => {
    if (visitorId) {
        const cookieHoursLifeTime = process.env
            .ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS
            ? parseInt(process.env.ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS, 10)
            : 1;

        setCookie(`_tf_visitorId`, visitorId, {
            req,
            res,
            maxAge: 60 * 60 * cookieHoursLifeTime,
            ...getSessionCookieOptions(),
        });
    }
};

export const setVisitId = (
    visitId: string | null,
    req:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res: ServerResponse,
): void => {
    if (visitId) {
        const cookieHoursLifeTime = process.env
            .ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS
            ? parseInt(process.env.ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS, 10)
            : 1;

        setCookie(`_tf_visitId`, visitId, {
            req,
            res,
            maxAge: 60 * 60 * cookieHoursLifeTime,
            ...getSessionCookieOptions(),
        });
    }
};

export const setAffiliateListIdCookie = (
    affiliateListId: string | number | null,
    req:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res: ServerResponse | undefined,
): void => {
    if (affiliateListId) {
        setCookie(`_affiliateListReqId`, affiliateListId, {
            req,
            res,
        });
    } else {
        deleteCookie(`_affiliateListReqId`, { req, res });
    }
};

export const setListIdCookie = (
    listId: string | number | null,
    req:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res: ServerResponse | undefined,
): void => {
    if (listId) {
        setCookie(`_listReqId`, listId, {
            req,
            res,
        });
    } else {
        deleteCookie(`_listReqId`, { req, res });
    }
};
export const setTimeOnSiteCookie = (
    tos: number,
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse | undefined,
): void => {
    const cookieHoursLifeTime = process.env
        .ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS
        ? parseInt(process.env.ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS, 10)
        : 1;
    setCookie(`_TOS`, tos, {
        req,
        res,
        maxAge: 60 * 60 * cookieHoursLifeTime,
    });
};

export const getAccessToken = (
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): CookieValueTypes => {
    return getCookie("_tf_token", { req, res });
};

export const getUserState = (
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): CookieValueTypes => {
    const state = getCookie("_tf_state", { req, res });
    return state;
};

export const getTimeOnSite = (
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): CookieValueTypes => {
    const tos = getCookie("_TOS", { req, res });
    return tos;
};

export const setLanderCookie = (
    value: string,
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): void => {
    const cookieHoursLifeTime = process.env.FB_LANDER_EXPIRATION_TIME_IN_HOURS
        ? parseInt(process.env.FB_LANDER_EXPIRATION_TIME_IN_HOURS, 10)
        : 1;
    setCookie("_fb_lander", value, {
        req,
        res,
        maxAge: 60 * 60 * cookieHoursLifeTime * 10,
    });
};

export const getLanderCookie = (
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): CookieValueTypes => {
    const lander = getCookie("_fb_lander", { req, res });
    return lander;
};

export const getVisitorId = (
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): CookieValueTypes => {
    const visitorId = getCookie("_tf_visitorId", {
        req,
        res,
    });
    return visitorId;
};

export const getVisitId = (
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): CookieValueTypes => {
    const visitId = getCookie("_tf_visitId", { req, res });
    return visitId;
};

export const getListId = (
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): string | undefined => {
    try {
        const listId = getCookie("_listReqId", { req, res });

        // Return undefined if cookie doesn't exist or is not a string
        if (typeof listId !== "string") {
            return undefined;
        }

        return listId;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error getting list ID:", error);
        return undefined;
    }
};

export const getAffiliateListId = (
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): string | undefined => {
    try {
        const affiliateListId = getCookie("_affiliateListReqId", { req, res });

        // Return undefined if cookie doesn't exist or is not a string
        if (typeof affiliateListId !== "string") {
            return undefined;
        }

        return affiliateListId;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error getting affiliate list ID:", error);
        return undefined;
    }
};

export const setTestVariantId = (
    testType: "form" | "style" | "template",
    variantId: string | null,
    categorySlug: string | null,
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): void => {
    if (variantId) {
        const key = `_tf_${testType}_${categorySlug ?? ""}_variant_id`;

        const cookieHoursLifeTime = process.env
            .ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS
            ? parseInt(process.env.ACCESS_TOKEN_EXPIRATION_TIME_IN_HOURS, 10)
            : 1;

        setCookie(key, variantId, {
            req,
            res,
            maxAge: 60 * 60 * cookieHoursLifeTime,
        });
    }
};

export const getTestVariantId = (
    testType: "form" | "style" | "template",
    categorySlug: string | null,
    req?:
        | (IncomingMessage & {
              cookies: NextApiRequestCookies;
          })
        | undefined,
    res?: ServerResponse,
): CookieValueTypes => {
    const key = `_tf_${testType}_${categorySlug ?? ""}_variant_id`;

    const variantId = getCookie(key, { req, res });
    return variantId;
};
